import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';
import debounce from 'lodash/debounce';

import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';

export const MIN_NOTES_HEIGHT_PX = 36;
export const NOTES_KEYBOARD_DEBOUNCE_MS = 150;

const getPx = (value: string): number => Number.parseFloat(value) || 0;

const useStyles = makeStyles()((theme) => ({
  field: {
    flex: 1,
    minWidth: 180,
    '& .MuiFilledInput-root': {
      borderRadius: theme.shape.borderRadius * 3,
      backgroundColor: theme.palette.background.default,
    },
    '& .MuiFilledInput-input': {
      paddingTop: theme.spacing(1.125),
      paddingBottom: theme.spacing(1.125),
    },
    '& .MuiFilledInput-input::placeholder': {
      ...theme.typography.bodyRegular,
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  fieldMultiline: {
    minHeight: MIN_NOTES_HEIGHT_PX,
    '& .MuiFilledInput-root': {
      alignItems: 'flex-start',
      minHeight: MIN_NOTES_HEIGHT_PX,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    '& .MuiInputAdornment-root': {
      marginTop: theme.spacing(0.25),
      alignSelf: 'flex-start',
    },
    '& .MuiFilledInput-input': {
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
    },
  },
  adornmentIcon: {
    color: theme.palette.icon.main,
    width: 18,
    height: 18,
  },
}));
type Props = {
  id?: string;
  className?: string;
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Series split dialog: grow vertically with content instead of a single scrolling line */
  multiline?: boolean;
};

const NotesField = ({
  id = 'split-note',
  className,
  value = '',
  onChange,
  disabled = false,
  multiline = false,
}: Props): JSX.Element => {
  const { classes, cx } = useStyles();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [draftValue, setDraftValue] = useState(value ?? '');

  useEffect(() => {
    setDraftValue(value ?? '');
  }, [value]);

  const debouncedOnChange = useMemo(
    () => debounce((nextValue: string) => onChange(nextValue), NOTES_KEYBOARD_DEBOUNCE_MS),
    [onChange],
  );

  useEffect(
    () => () => {
      debouncedOnChange.cancel();
    },
    [debouncedOnChange],
  );

  useLayoutEffect(() => {
    if (!multiline) return;
    const el = inputRef.current;
    if (!el || el.tagName !== 'TEXTAREA') return;
    const ta = el as HTMLTextAreaElement;
    const inputRoot = ta.closest('.MuiFilledInput-root');
    const textareaStyles = window.getComputedStyle(ta);
    const textareaVerticalPadding = getPx(textareaStyles.paddingTop) + getPx(textareaStyles.paddingBottom);
    const inputRootStyles = inputRoot ? window.getComputedStyle(inputRoot) : null;
    const inputRootVerticalPadding = inputRootStyles
      ? getPx(inputRootStyles.paddingTop) + getPx(inputRootStyles.paddingBottom)
      : 0;
    const minTextareaHeight = Math.max(0, MIN_NOTES_HEIGHT_PX - inputRootVerticalPadding - textareaVerticalPadding);
    ta.style.overflow = 'hidden';
    ta.style.resize = 'none';
    ta.style.height = '0px';
    ta.style.height = `${Math.max(minTextareaHeight, ta.scrollHeight - textareaVerticalPadding)}px`;
  }, [draftValue, multiline]);

  const handleChange = (nextValue: string): void => {
    setDraftValue(nextValue);
    debouncedOnChange(nextValue);
  };

  const handleBlur = (): void => {
    debouncedOnChange.flush();
  };

  return (
    <TextField
      id={`${id}-input`}
      inputRef={inputRef}
      hiddenLabel
      placeholder='Note'
      variant='filled'
      size='small'
      multiline={multiline}
      rows={multiline ? 1 : undefined}
      value={draftValue}
      onChange={(event) => handleChange(event.target.value)}
      onBlur={handleBlur}
      disabled={disabled}
      className={cx(classes.field, multiline && classes.fieldMultiline, className)}
      fullWidth={multiline}
      inputProps={{ maxLength: 512 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <EditNoteOutlinedIcon fontSize='small' className={classes.adornmentIcon} />
          </InputAdornment>
        ),
      }}
    />
  );
};

export default NotesField;
